import React, { createContext, useContext, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

const cmp = (a, b, num) => {
  if (num) return (Number(a) || 0) - (Number(b) || 0);
  return String(a ?? "").trim().localeCompare(String(b ?? "").trim(), "tr", { numeric: true, sensitivity: "base" });
};
const isNum = (v) => typeof v === "number";
const keyOf = (r) => r?.id ?? r?.personnel_id;

function sortRows(rows, sort, getters, numericKeys) {
  const get = getters[sort.k] || ((r) => r?.[sort.k]);
  const vals = rows.map(get);
  const num = numericKeys.includes(sort.k) || vals.every((v) => v == null || v === "" || isNum(v));
  const dir = sort.dir === "asc" ? 1 : -1;
  return rows.map((r, i) => [r, vals[i]]).sort(([, a], [, b]) => {
    const ea = a === "" || a == null || (typeof a === "string" && !a.trim()), eb = b === "" || b == null || (typeof b === "string" && !b.trim());
    if (ea !== eb) return ea ? 1 : -1;
    return dir * cmp(Array.isArray(a) ? a.join(", ") : a, Array.isArray(b) ? b.join(", ") : b, num);
  }).map(([r]) => r);
}

// Click a header: ascending → descending → default order.
// getters: optional {key: (row) => value} for computed columns; numbers auto-detected.
// freeze: keep the row order fixed while values are edited inline (re-sorts on header click / row set change).
export function useColumnSort(rows, numericKeys = [], getters = {}, freeze = false) {
  const [sort, setSort] = useState(null);
  const frozen = useRef({ sort: null, sig: "", order: [] });
  const sorted = useMemo(() => {
    if (!sort || !Array.isArray(rows)) return rows;
    if (!freeze) return sortRows(rows, sort, getters, numericKeys);
    const sig = rows.map(keyOf).sort().join("|");
    const f = frozen.current;
    if (f.sort !== sort || f.sig !== sig) {
      frozen.current = { sort, sig, order: sortRows(rows, sort, getters, numericKeys).map(keyOf) };
    }
    const byKey = new Map(rows.map((r) => [keyOf(r), r]));
    return frozen.current.order.map((k) => byKey.get(k)).filter(Boolean);
  }, [rows, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (k) => setSort((s) => (!s || s.k !== k ? { k, dir: "asc" } : s.dir === "asc" ? { k, dir: "desc" } : null));
  return { sorted, sort, toggle };
}

const SortCtx = createContext(null);
export const SortProvider = ({ value, children }) => <SortCtx.Provider value={value}>{children}</SortCtx.Provider>;

// Drop-in <th> replacement: <STh k="field" className=...>Label</STh> inside a <SortProvider value={{sort, toggle}}>.
export function STh({ k, children, className = "", style, title, testid }) {
  const ctx = useContext(SortCtx);
  if (!ctx) return <th className={className} style={style} title={title}>{children}</th>;
  const active = ctx.sort && ctx.sort.k === k;
  const Icon = !active ? ArrowUpDown : ctx.sort.dir === "asc" ? ArrowUp : ArrowDown;
  const right = /text-right/.test(className);
  return (
    <th className={className} style={style} title={title} aria-sort={active ? (ctx.sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => ctx.toggle(k)} data-testid={testid || `sort-${k}`}
        className={`inline-flex items-center gap-1 [text-transform:inherit] [letter-spacing:inherit] [font:inherit] [color:inherit] hover:opacity-80 ${right ? "flex-row-reverse" : ""}`}>
        {children}<Icon size={11} className={`shrink-0 ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </th>
  );
}

export function SortTh({ k, label, sort, onSort, className = "", testid }) {
  const active = sort && sort.k === k;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={className} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(k)} data-testid={testid || `sort-${k}`}
        className={`inline-flex items-center gap-1 [text-transform:inherit] [letter-spacing:inherit] hover:text-slate-900 ${active ? "text-slate-900" : ""}`} title="Sıralamak için tıklayın">
        {label}<Icon size={11} className={active ? "text-emerald-700" : "text-slate-400"} />
      </button>
    </th>
  );
}
